import api from 'api';
import ApiCaller from 'api/apiCaller';
import { assignIn, forEach, uniqBy } from 'lodash';
import { Button } from 'reactstrap';
import React, { Fragment, useState, useRef, useEffect } from 'react';
import { GraphView } from 'react-digraph';
import SelectTransformationModal from '../SelectTransformationModal';
import "./index.css";

/* 
TODO:
+ Modal to search for transformation
+ Add transformation to graph
*/
  
const NODE_KEY = "task_id";
const STROKE_WIDTH = 3;

const GraphConfig =  {
    NodeTypes: {
        task: {
            typeText: "task",
            shapeId: "#taskNode",
            shape: (
            <symbol viewBox="0 0 200 100" id="taskNode" key="0">
                <rect width="200" height="100" rx="4" fill="var(--color-line)"></rect>
            </symbol>
            ),
        },
        task_success: {
            typeText: "task_success",
            shapeId: "#successTaskNode",
            shape: (
            <symbol viewBox="0 0 200 100" id="successTaskNode" key="0">
                <rect width="200" height="100" rx="10" fill="var(--color-line)" stroke="green" strokeWidth={STROKE_WIDTH}></rect>
            </symbol>
            ),
        },
        task_failed: {
            typeText: "task_failed",
            shapeId: "#failedTaskNode",
            shape: (
            <symbol viewBox="0 0 200 100" id="failedTaskNode" key="0">
                <rect width="200" height="100" rx="10" fill="var(--color-line)" stroke="red" strokeWidth={STROKE_WIDTH}></rect>
            </symbol>
            ),
        },
        start: {
            typeText: "start",
            shapeId: "#startNode",
            shape: (
            <symbol viewBox="0 0 200 200" id="startNode" key="0">
                <circle cx="100" cy="100" r="50" fill="var(--color-line)" />
            </symbol>
            ),
        },
        end: {
            typeText: "end",
            shapeId: "#endNode",
            shape: (
            <symbol viewBox="0 0 200 200" id="endNode" key="0">
                <circle cx="100" cy="100" r="50" fill="var(--color-line)" />
            </symbol>
            ),
        },
    },
    NodeSubtypes: {},
    EdgeTypes: {
        composeEdge: {  // required to show empty edges
            shapeId: "#composeEdge",
            shape: (
            <symbol viewBox="0 0 50 50" id="composeEdge" key="0">
                <circle cx="25" cy="25" r="8" fill="currentColor"></circle>
            </symbol>
            )
        }
    }
};

const defaultGraph = {
    nodes: [],
    edges: [],
};
  
const DataPipelineGraphView = ({
    inputGraph, readOnly=false, dataPipeline,
}) => {
    const companyId = api.getCurrentCompanyId();
    const [selected, setSelected] = useState({});
    const [tasks, setTasks] = useState({});
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [graph, setGraph] = useState(defaultGraph);
    const [showTransformationSelectionModal, setShowTransformationSelectionModal] = useState(false);
    const [selectedTransformation, setSelectedTransformation] = useState([]);

    /* Define custom graph editing methods here */
    const renderNodeText = (data, id, isSelected) => {
        return (
            <text className="node-text" textAnchor="middle">
                <tspan x="0" dy="0" fontSize="14px" fontWeight="bold">
                    {data.task_name}
                </tspan>
                {!['start', 'end'].includes(data?.type) &&
                    <tspan x="0" dy="1.8em" fontSize="10px" opacity="0.5">
                        {data.status}
                    </tspan>
                }
            </text>
        );
    }
    const _onSelectNode = (node) => {
        setSelected(node);
        setSelectedNode(node);
    }
    const _onSelectEdge = (edge) => {
        setSelectedEdge(edge);
        setSelected(edge);
    }
    const _onCreateEdge = (fromNode, toNode) => {
        const newEdge = {
            source: fromNode.task_id,
            target: toNode.task_id,
        };
        setGraph({
            nodes: [...graph.nodes],
            edges: [...graph.edges, newEdge],
        });
    };
    const _onDeleteNode = (x, y) => {
        console.log('delete node', x, y);
    };
    const _onDeleteEdge = (x, y) => {
        console.log('delete edge', x, y);
    }; 
    const deleteSelected = () => {
        if (selectedNode) {
            console.log('graph.nodes', graph.nodes.filter(node => node.task_id != selectedNode.task_id));
            setGraph({
                nodes: graph.nodes.filter(node => node.task_id != selectedNode.task_id),
                // We need to remove the egdes related to the deleted node as well
                edges: graph.edges.filter(edge => (edge.source !== selectedNode.task_id) && (edge.target !== selectedNode.task_id)),
            });
            // Also remove it from the tasks state
            const newTasks = {...tasks};
            delete newTasks[selectedNode.task_id]; 
            setTasks(newTasks);
        }
        if (selectedEdge) {
            setGraph({
                ...graph,
                edges: graph.edges.filter(edge => (edge.source !== selectedEdge.source) && (edge.target !== selectedEdge.target)),
            });
        }
        setSelectedEdge(null);
        setSelectedNode(null);
    };
    const goToRunLogScreen = () => {
        window.location.href = `/data_pipeline_log/${dataPipeline.id}/task/${selectedNode.task_id}`;
    };
    const goToTransformation = () => {
        window.location.href = `/transformation/${selectedNode.transformation_id}`;
    };
    const toggleSelectTransformationModal = () => {
        setShowTransformationSelectionModal(!showTransformationSelectionModal);
    };
    const generateTransformationGraph = () => {
        const nodes = [...graph.nodes] || [];
        selectedTransformation.forEach((transformation) => {
            const transformationCodeNameFormatted = `${transformation?.code_name?.replace(':', '_')}`;
            const newNode = {
                task_id: `task_transformation_${transformation.id}_${transformationCodeNameFormatted}`,
                task_name: transformation.name,
                type: 'task',
                pipeline_id: dataPipeline.id,
                transformation_id: transformation.id,
            };
            nodes.push(newNode);
        });
        setGraph({
            ...graph,
            nodes,
        });
    };
    const generateTaskDefinition = async () => {
        graph.nodes.forEach((node) => {
            tasks[node.task_id] = {
                'operator': 'DataDexTransformationOperator',
                'transformation_id': node.transformation_id,
            };
        });
        graph.edges.forEach((edge) => {
            for (const [task_id, task] of Object.entries(tasks)) {
                if (task_id == edge.target) {
                    const currentDependencies = tasks[task_id]?.dependencies || [];
                    tasks[task_id] = {
                        ...task,
                        dependencies: [...currentDependencies, edge.source],
                    }; 
                }  
            }
        });
        const dagSpecs = {
            tasks,
        };
        // console.log('dagSpecs', dagSpecs);
        const uri = `pipeline/${dataPipeline.id}/tasks?company_id=${companyId}`;
        const res = await ApiCaller.put(
            uri,
            dagSpecs,
        );
        if (res.status === 'ok') {
            alert('Data pipline is updated');
        }
    };
    const onSelectedTransformationConfirm = (transformations) => {
        setSelectedTransformation(uniqBy([
            ...selectedTransformation,
            ...transformations,
        ], 'id'));
    };
    useEffect(() => {
        if (inputGraph && inputGraph.nodes) {
            setGraph(inputGraph);
        }
    }, [inputGraph]);
    useEffect(() => {
        generateTransformationGraph();
    }, [selectedTransformation]);
    useEffect(() => {
        if (dataPipeline.tasks) {
            setTasks(dataPipeline.tasks);
        }
    }, [dataPipeline]);
    useEffect(() => {
        console.log('graph', graph);
    }, [graph]);
    const NodeTypes = GraphConfig.NodeTypes;
    const NodeSubtypes = GraphConfig.NodeSubtypes;
    const EdgeTypes = GraphConfig.EdgeTypes;
    const graphRef = useRef();
    return (
        <div id='graph' className="data-pipeline-graph__container">
            <div className="data-pipeline-graph__controls">
                {dataPipeline.data_pipeline_template_name === "generic_transformation" &&
                    <div className="data-pipeline-graph__controls__button">
                        <Button
                            size="sm"
                            color="info"
                            onClick={toggleSelectTransformationModal}
                        >
                            Add new transformation
                        </Button>
                    </div>
                }
                {dataPipeline.data_pipeline_template_name === "generic_transformation" &&
                    <div className="data-pipeline-graph__controls__button">
                        <Button
                            size="sm"
                            color="info"
                            onClick={deleteSelected}
                        >
                            Delete tasks
                        </Button>
                    </div>
                }
                {selectedNode &&
                    <Fragment>
                        {dataPipeline.data_pipeline_template_name === "generic_transformation" &&
                            <div className="data-pipeline-graph__controls__button">
                                <Button
                                    size="sm"
                                    color="info"
                                    onClick={goToTransformation}
                                >
                                    View transformation
                                </Button>
                            </div>
                        }
                        <div className="data-pipeline-graph__controls__button">
                            <Button
                                size="sm"
                                color="info"
                                onClick={goToRunLogScreen}
                            >
                                View task log
                            </Button>
                        </div>
                    </Fragment>
                }
                {dataPipeline.data_pipeline_template_name === "generic_transformation" &&
                    <div className="data-pipeline-graph__controls__button">
                        <Button
                            size="sm"
                            color="success"
                            onClick={generateTaskDefinition}
                        >
                            Save pipeline tasks
                        </Button>
                    </div>
                }
            </div>
            <GraphView
                ref={graphRef}
                layoutEngineType='HorizontalTree'
                nodeLocationOverrides
                readOnly={readOnly}
                minZoom={0.5}
                gridDotSize={0}
                gridSpacing={100}
                edgeHandleSize={0}
                maxTitleChars={300}
                nodeKey={NODE_KEY}
                nodes={[...graph?.nodes] || []}
                edges={[...graph?.edges] || []}
                selected={selected}
                nodeTypes={NodeTypes}
                nodeSubtypes={NodeSubtypes}
                edgeTypes={EdgeTypes}
                onSelectNode={_onSelectNode}
                onSelectEdge={_onSelectEdge}
                canDeleteSelected={true}
                onCreateEdge={_onCreateEdge}
                renderNodeText={renderNodeText}
                onDeleteEdge={_onDeleteEdge}
                onDeleteNode={_onDeleteNode}
            />
            {showTransformationSelectionModal &&
                <SelectTransformationModal
                    isOpen={showTransformationSelectionModal}
                    onSelectedTransformationConfirm={onSelectedTransformationConfirm}
                    onModalClose={() => setShowTransformationSelectionModal(false)}
                />
            }
        </div>
    );
}

export default DataPipelineGraphView;